if (!customElements.get('raceit-banner-slideshow')) {
  customElements.define('raceit-banner-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('.trk');
      this.slides = [...this.querySelectorAll('.sld')];
      this.dots = [...this.querySelectorAll('.dot')];
      this.progress = this.querySelector('.prog');
      this.mobile = matchMedia('(max-width:768px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion:reduce)');
      this.touch = matchMedia('(hover:none),(pointer:coarse)');
      this.delay = Number(this.dataset.autoplayMs) || 5000;
      this.auto = !this.mobile.matches && !this.reducedMotion.matches && !Shopify.designMode;
      this.hovering = false;
      this.focused = false;
      this.index = this.firstIndex();
      this.show(this.index);
      this.track.style.transition = 'none';
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (button?.hasAttribute('data-next')) this.show(this.index + 1);
        else if (button?.hasAttribute('data-previous')) this.show(this.index - 1);
        else if (button?.hasAttribute('data-i')) this.show(Number(button.dataset.i));
        else return;
        this.afterInteraction();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
          this.afterInteraction();
        }
      }, options);
      this.addEventListener('touchstart', event => {
        const touch = event.changedTouches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY };
      }, { ...options, passive: true });
      this.addEventListener('touchend', event => {
        if (!this.touchStart) return;
        const touch = event.changedTouches[0];
        const dx = this.touchStart.x - touch.clientX;
        const dy = this.touchStart.y - touch.clientY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.show(this.index + (dx > 0 ? 1 : -1));
          this.afterInteraction();
        }
        this.touchStart = null;
      }, { ...options, passive: true });
      this.addEventListener('mouseenter', () => { this.hovering = true; this.stop(); }, options);
      this.addEventListener('mouseleave', () => { this.hovering = false; this.start(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.stop(); }, options);
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) { this.focused = false; this.start(); }
      }, options);
      this.mobile.addEventListener('change', () => {
        this.auto = !this.mobile.matches && !this.reducedMotion.matches && !Shopify.designMode;
        this.show(this.index);
        this.start();
      }, options);
      this.reducedMotion.addEventListener('change', () => {
        this.auto = !this.mobile.matches && !this.reducedMotion.matches && !Shopify.designMode;
        this.start();
      }, options);
      document.addEventListener('visibilitychange', () => this.start(), options);
      document.addEventListener('shopify:block:select', event => {
        const selected = this.slides.findIndex(slide => slide.dataset.bannerBlock === event.detail?.blockId);
        if (selected >= 0) {
          this.editorSelection = true;
          this.auto = false;
          this.stop();
          this.show(selected);
        }
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        if (this.slides.some(slide => slide.dataset.bannerBlock === event.detail?.blockId)) {
          this.editorSelection = false;
          this.show(this.index);
        }
      }, options);
      this.start();
      // Once the page has settled, get the next slide ready for a swipe, or every slide when
      // autoplay (desktop) will cycle through them.
      const settle = () => setTimeout(() => {
        this.settled = true;
        if (this.auto) this.loadAllSlides();
        else this.loadNextSlide();
      }, 2500);
      if (document.readyState === 'complete') settle();
      else window.addEventListener('load', settle, { once: true, signal: this.controller.signal });
    }
    firstIndex() {
      return this.mobile.matches && this.hasAttribute('data-skip-first-mobile') && this.slides.length > 1 && !this.editorSelection ? 1 : 0;
    }
    show(next) {
      const first = this.firstIndex();
      const count = this.slides.length - first;
      if (!this.track || count < 1) return;
      this.index = first + (((next - first) % count) + count) % count;
      this.loadSlide(this.index);
      if (this.settled) this.loadNextSlide();
      this.track.style.transition = this.reducedMotion.matches ? 'none' : 'transform .6s cubic-bezier(.4,0,.2,1)';
      this.track.style.transform = `translateX(-${this.index * 100 / this.slides.length}%)`;
      this.slides.forEach((slide, index) => {
        if (index !== this.index && slide.contains(document.activeElement)) this.dots[this.index]?.focus({ preventScroll: true });
        slide.inert = index !== this.index;
        slide.setAttribute('aria-hidden', String(index !== this.index));
      });
      this.dots.forEach((dot, index) => {
        dot.hidden = index < first;
        dot.classList.toggle('on', index === this.index);
        dot.setAttribute('aria-current', String(index === this.index));
        if (index === this.index) {
          const container = dot.parentElement;
          const a = dot.getBoundingClientRect(), b = container.getBoundingClientRect();
          if (a.left < b.left) container.scrollLeft -= b.left - a.left;
          if (a.right > b.right) container.scrollLeft += a.right - b.right;
        }
      });
    }
    // Slides from the third on are rendered with data-src/data-srcset so they do not compete
    // with the first screen; give them their real sources when they are about to be shown.
    loadSlide(index) {
      this.slides[index]?.querySelectorAll('[data-srcset], [data-src]').forEach(el => {
        if (el.dataset.srcset) el.srcset = el.dataset.srcset;
        if (el.dataset.src) el.src = el.dataset.src;
        el.removeAttribute('data-srcset');
        el.removeAttribute('data-src');
      });
    }
    loadNextSlide() {
      const first = this.firstIndex();
      const count = this.slides.length - first;
      if (count > 1) this.loadSlide(first + ((this.index - first + 1) % count));
    }
    loadAllSlides() {
      this.slides.forEach((slide, index) => this.loadSlide(index));
    }
    afterInteraction() {
      this.settled = true;
      this.loadNextSlide();
      if (this.touch.matches) this.auto = false;
      this.start();
    }
    start() {
      this.stop();
      if (!this.auto || this.hovering || this.focused || document.hidden || this.slides.length < 2) return;
      this.timer = setInterval(() => { this.show(this.index + 1); this.startProgress(); }, this.delay);
      this.startProgress();
    }
    startProgress() {
      clearInterval(this.progressTimer);
      if (!this.progress) return;
      const started = Date.now();
      this.progress.style.width = '0%';
      this.progressTimer = setInterval(() => {
        this.progress.style.width = `${Math.min((Date.now() - started) / this.delay * 100, 100)}%`;
      }, 30);
    }
    stop() {
      clearInterval(this.timer);
      clearInterval(this.progressTimer);
      if (this.progress) this.progress.style.width = '0%';
    }
    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
    }
  });
}
